/**
 * Shared landing page body — used by the public page and the admin layout preview.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card } from '../../components/ui'
import { BrandBanner, QardHasanLogo } from '../../components/Brand'
import type { LandingSectionId } from '../../types/firestore'
import type { PublicSavingsPlan } from '../../lib/savingsPlans'
import type { PublicFundImpact } from './useSavingsPlans'
import { PREVIEW_FUND_IMPACT } from './useSavingsPlans'
import { FundImpactCard } from './FundImpactCard'
import { SavingsPlansBanner } from './SavingsPlansBanner'

function Reveal({
  children,
  delay = 0,
  preview = false,
}: {
  children: ReactNode
  delay?: number
  preview?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (preview) return
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          el.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [preview])

  return (
    <div
      ref={ref}
      className={['landing-reveal', preview && 'is-visible'].filter(Boolean).join(' ')}
      style={{ transitionDelay: preview ? '0ms' : `${delay}ms` }}
    >
      {children}
    </div>
  )
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="group flex gap-3">
      <span
        aria-hidden="true"
        className="landing-step-badge mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-brand-800"
      >
        {n}
      </span>
      <div>
        <p className="font-semibold text-stone-900 transition-colors group-hover:text-brand-800">
          {title}
        </p>
        <p className="text-sm text-stone-600">{body}</p>
      </div>
    </li>
  )
}

function ScriptureQuote({
  reference,
  arabic,
  text,
}: {
  reference: string
  arabic?: string
  text: string
}) {
  return (
    <blockquote className="flex flex-col gap-3 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-stone-100 [&:not(:last-child)]:pb-5">
      <p className="text-xs font-semibold tracking-wide text-brand-800 uppercase">
        {reference}
      </p>
      {arabic && (
        <div className="scripture-arabic-panel rounded-xl border border-brand-200/60 px-4 py-5">
          <p dir="rtl" lang="ar" className="arabic-scripture">
            {arabic}
          </p>
        </div>
      )}
      <p className="text-sm leading-relaxed text-stone-700 italic">{text}</p>
    </blockquote>
  )
}

export interface LandingPageContentProps {
  sectionOrder: LandingSectionId[]
  plans: PublicSavingsPlan[]
  plansLoading?: boolean
  fundImpact?: PublicFundImpact
  /** Static preview for admin — no scroll reveals or navigation. */
  preview?: boolean
}

export function LandingPageContent({
  sectionOrder,
  plans,
  plansLoading = false,
  fundImpact,
  preview = false,
}: LandingPageContentProps) {
  const { t } = useTranslation()
  const impact = preview ? (fundImpact ?? PREVIEW_FUND_IMPACT) : fundImpact

  function renderSection(id: LandingSectionId): ReactNode {
    switch (id) {
      case 'savingsPlans':
        if (plans.length === 0) return null
        return (
          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-stone-900">
              {t('savingsPlans.landingTitle')}
            </h2>
            <p className="text-sm text-stone-600">{t('savingsPlans.landingIntro')}</p>
            <SavingsPlansBanner plans={plans} loading={plansLoading} preview={preview} />
          </section>
        )
      case 'brandBanner':
        return <BrandBanner title={t('app.name')} tagline={t('app.bannerTagline')} />
      case 'fundImpact':
        return impact ? <FundImpactCard fundImpact={impact} /> : null
      case 'scripture':
        return (
          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-stone-900">
              {t('landing.scriptureTitle')}
            </h2>
            <p className="text-sm text-stone-600">{t('landing.scriptureIntro')}</p>
            <Card className="landing-card flex flex-col gap-5 border-brand-100/80">
              <ScriptureQuote
                reference={t('landing.quran1Ref')}
                arabic={t('landing.quran1Arabic')}
                text={t('landing.quran1Text')}
              />
              <ScriptureQuote
                reference={t('landing.quran2Ref')}
                arabic={t('landing.quran2Arabic')}
                text={t('landing.quran2Text')}
              />
              <ScriptureQuote reference={t('landing.hadithRef')} text={t('landing.hadithText')} />
            </Card>
          </section>
        )
      case 'noInterest':
        return (
          <Card className="landing-card relative overflow-hidden border-gold-300 bg-linear-to-br from-gold-100 via-gold-50 to-white">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-6 -right-6 size-24 rounded-full bg-gold-300/40 blur-2xl"
            />
            <p className="relative font-semibold text-stone-900">
              {t('landing.noInterestTitle')}
            </p>
            <p className="relative mt-1 text-sm text-stone-700">{t('landing.noInterestBody')}</p>
          </Card>
        )
      case 'tagline':
        return (
          <p className="text-center text-base leading-relaxed text-stone-600">
            {t('app.tagline')}
          </p>
        )
      case 'howItWorks':
        return (
          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-stone-900">
              {t('landing.howItWorks')}
            </h2>
            <Card className="landing-card">
              <ol className="flex flex-col gap-5">
                <Step n={1} title={t('landing.step1Title')} body={t('landing.step1Body')} />
                <Step n={2} title={t('landing.step2Title')} body={t('landing.step2Body')} />
                <Step n={3} title={t('landing.step3Title')} body={t('landing.step3Body')} />
                <Step n={4} title={t('landing.step4Title')} body={t('landing.step4Body')} />
              </ol>
            </Card>
          </section>
        )
      case 'applyCtas':
        if (preview) {
          return (
            <div className="flex flex-col gap-3 pt-1">
              <div className="landing-cta-primary flex items-center justify-center rounded-xl px-5 py-3.5 text-base font-bold text-white">
                {t('landing.applyCta')}
              </div>
              <div className="flex items-center justify-center rounded-xl border border-stone-300/80 bg-white/90 px-5 py-3.5 text-base font-semibold text-brand-800 shadow-sm">
                {t('landing.trackCta')}
              </div>
            </div>
          )
        }
        return (
          <div className="flex flex-col gap-3 pt-1">
            <Link
              to="/apply"
              className="landing-cta-primary tap-target flex items-center justify-center rounded-xl px-5 py-3.5 text-base font-bold text-white"
            >
              {t('landing.applyCta')}
            </Link>
            <Link
              to="/track"
              className="tap-target flex items-center justify-center rounded-xl border border-stone-300/80 bg-white/90 px-5 py-3.5 text-base font-semibold text-brand-800 shadow-sm backdrop-blur transition-[transform,box-shadow,background-color] duration-300 hover:bg-white hover:shadow-md active:scale-[0.98]"
            >
              {t('landing.trackCta')}
            </Link>
          </div>
        )
      case 'logo':
        return (
          <div className="flex justify-center border-t border-stone-200/80 pt-6">
            <QardHasanLogo className={preview ? undefined : 'motion-safe:animate-[fade-in_1s_ease-out]'} />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-7">
      {sectionOrder.map((id, index) => {
        const content = renderSection(id)
        if (!content) return null

        if (id === 'brandBanner') {
          return <div key={id}>{content}</div>
        }

        return (
          <Reveal key={id} delay={Math.min(index * 40, 240)} preview={preview}>
            {content}
          </Reveal>
        )
      })}
    </div>
  )
}
